// Angular imports
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewChild
} from '@angular/core';

// CC-Common imports
import {
  CCMessageBoxIcon,
  IconConstants,
  MessageBoxComponent
} from '@therealmanage/ciranet-ui-common';
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { UserService } from '../../_services/data-services/user.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';

// Model imports
import { UserDashboardItemResultDto } from '../../_models/user-dashboard-item-result-dto';
import { ActivityFeedResultDto } from '../../_models/activity-feed-result-dto';
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';
import { UserDashboardItemDetailResultDto } from '../../_models/user-dashboard-item-detail-result-dto';

@Component({
  selector: 'app-user-dashboard-item',
  templateUrl: './user-dashboard-item.component.html',
  styleUrls: ['./user-dashboard-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UserDashboardItemComponent implements OnInit {
  @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;

  @Input() elementId: number;
  @Input() dashboardItem: UserDashboardItemResultDto;
  @Input() isQuickLinks = false;
  @Input() isActivityList = false;
  @Input() isCompact = false;
  @Output() navigateToLink: EventEmitter<any> = new EventEmitter();

  isSmallScreen = false;
  isMediumScreen = false;
  popoverVisible = false;
  loadingVisible = false;
  popoverHeight = 10;

  selectedProperty: UserPropertyResultDto;
  popoverDashboardItem: UserDashboardItemResultDto;

  iconExclamatory = IconConstants.EXCLAMATION_CIRCLE;

  constructor(
    private screenService: ScreenService,
    private userService: UserService,
    private helperService: HelperService,
    private errorService: ServerErrorService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );

    this.selectedProperty = this.userService.getSelectedProperty();
  }

  togglePopoverVisibility(childItem?: UserDashboardItemResultDto): void {
    this.popoverVisible = false;
    const dashboardItem = childItem || this.dashboardItem;
    if (
      !!dashboardItem.details &&
      !(this.isQuickLinks && this.isActivityList)
    ) {
      if (dashboardItem.details.length > 0) {
        if (dashboardItem.details.length > 1) {
          this.popoverDashboardItem = dashboardItem;
          this.popoverHeight = 30 + dashboardItem.details.length * 45;
          this.popoverVisible = true;
        } else {
          const data = dashboardItem.details[0];
          this.navigateFromGridRow(data, dashboardItem);
        }
      } else {
        this.navigateFromGridRow(null, dashboardItem);
      }
    } else if (dashboardItem.code === 'announcement') {
      this.navigateToLink.emit({
        code: dashboardItem.code,
        data: dashboardItem
      });
    }
  }

  navigateFromActivity(code: string, data: ActivityFeedResultDto): void {
    const rec = (data.details || []).find(
      (r) => r && r.valueType === 'DOWNLOAD'
    );
    if (!!rec && !!rec.value) {
      this.loadingVisible = true;
      let fileName = '';
      if (code === 'my-doc') {
        fileName = rec.value.split('&')[1].replace('FileName=', '');
        RxjsUtils.safeSubscribe(
          this.helperService.getImageBlobFromUrl(rec.value),
          (fileData) => {
            this.loadingVisible = false;
            this.cdr.markForCheck();
            this.helperService.downloadFileUsingBlob(fileData, fileName);
          },
          (error) => {
            this.loadingVisible = false;
            this.cdr.markForCheck();
            this.errorService.displayError(error);
          }
        );
      } else if (code === 'account-statement') {
        const queryParamsMap = {};
        rec.value
          .split('?')[1]
          .split('&')
          .forEach((qp) => {
            const [key, value] = qp.split('=');
            queryParamsMap[key] = value;
          });
        fileName = `${data.customerId}-${queryParamsMap['TransactionType']}-${queryParamsMap['TransactionId']}-${queryParamsMap['DocumentDate']}`;
        RxjsUtils.safeSubscribe(
          this.helperService.showUrlInBrowserTab(rec.value, fileName),
          (result) => {
            this.loadingVisible = false;
            this.cdr.markForCheck();
            // Expected outcome with no document (e.g. an ACH payment has no
            // check image): the API returns 200 with an informational message.
            if (result?.info) {
              this.errorService.updateSubjectMessage({
                title: 'Information',
                message: result.info,
                icon: CCMessageBoxIcon.info
              });
            }
            // this.navigateToLink.emit({ code, data }); Do not redirect to statement page in case of success
          },
          (error) => {
            this.loadingVisible = false;
            this.cdr.markForCheck();
            this.navigateToLink.emit({ code, data }); // Redirect to statement page in case download issue
            // Handle error for special cases. The status-400 INFO branch is
            // legacy back-compat for API versions that returned informational
            // messages as a 400; current API versions return them as a 200.
            let message: any = error.error || error.message;
            if (error.status === 400 && message) {
              if (message.byteLength) {
                const msg = new TextDecoder('utf-8').decode(message);
                try {
                  const json = JSON.parse(msg);
                  message = json.message || json.error;
                } catch {
                  message = msg;
                }
              }
              if (message.startsWith('INFO:')) {
                this.errorService.updateSubjectMessage({
                  title: 'Information',
                  message: message.replace('INFO:', ''),
                  icon: CCMessageBoxIcon.info
                });
              } else {
                this.errorService.displayError(error);
              }
            } else {
              this.errorService.displayError(error);
            }
          }
        );
      }
    } else if (code !== 'my-doc') {
      this.navigateToLink.emit({ code, data });
    }
  }

  navigateFromGridRow(
    data?: any,
    dashboardItem?: UserDashboardItemResultDto
  ): void {
    const code = (dashboardItem || this.dashboardItem).code;
    this.navigateToLink.emit({ code, data });
  }

  onImportantMessageClick(row: UserDashboardItemDetailResultDto): void {
    this.messageBox.showInfoMessage('Account Balance Message', row.value);
  }

  getFormattedValue(val: string): string {
    const valToChange = parseFloat(val);

    return this.helperService.getCurrencyStringFormat(valToChange);
  }

  onViewDocumentClick(fileName: string, fileUrl: string): void {
    this.openFileInTab(fileName, fileUrl);
  }

  onViewNoticeClick(fileName: string, fileUrl: string): void {
    this.openFileInTab(fileName, fileUrl);
  }

  private openFileInTab(fileName: string, fileUrl: string): void {
    this.loadingVisible = true;
    RxjsUtils.safeSubscribe(
      this.helperService.showUrlInBrowserTab(fileUrl, fileName),
      // View Document / View Notice endpoints always return a binary document,
      // never an informational 200, so BrowserTabResult.info is intentionally
      // not surfaced here (unlike the transaction-document flow above). See RES-618.
      () => {
        this.loadingVisible = false;
        this.cdr.markForCheck();
      },
      (error) => {
        this.errorService.displayError(error);
        this.loadingVisible = false;
        this.cdr.markForCheck();
      }
    );
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByKey(_: number, rec: any): any {
    return rec.key;
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    this.isMediumScreen = this.screenService.isMediumScreen();
    this.cdr.markForCheck();
  }
}
